import { Note, Topic } from '@/types'
import { PrivacyType } from '@/utils/constants'
import { getFullImageURL } from '@/utils/helper'
import { getClassicVariant } from '@/features/gallery-studio-3d/classicCatalog'
import { STUDIO3D_NOTE_NAME } from '@/features/gallery-studio-3d/constants'
import type { StudioSceneState } from '@/features/gallery-studio-3d/types'
import {
  parseAllowDownload,
  parseCoverEgoId,
  parseFullResolutionUploads,
  parseGalleryId,
  parsePhotoOrder,
} from './utils/albumXData'
import type { AlbumDetail, AlbumPhoto, AlbumPrivacy, AlbumSummary, GalleryArtPreview, GallerySummary } from './types'

export function mapPrivacy(visibility?: string): AlbumPrivacy {
  if (visibility === String(PrivacyType.PUBLIC)) return 'public'
  return 'private'
}

export function privacyToAccessType(privacy: AlbumPrivacy): number {
  return privacy === 'public' ? PrivacyType.PUBLIC : PrivacyType.PRIVATE
}

/**
 * Best photo URL from a topicList/detail payload — albums and galleries
 * share this shape. Honors an owner-picked `coverEgoId` (topicXData) before
 * falling back to latestEgo/first photo.
 *
 * `photoThumbURL` is a small crop meant for grid tiles, not a full-width
 * hero banner — using it there is what made album headers look pixelated,
 * since a ~150px thumbnail was getting stretched across ~1180px. Prefer
 * `photoFullURL`/`photoAlbumURL` (the real, larger variants) and only fall
 * back to the thumb if nothing bigger exists.
 */
export function topicCoverUrl(topic: Topic): string | undefined {
  const coverEgoId = parseCoverEgoId(topic.topicXData)
  if (coverEgoId) {
    const fromPhotos = topic.photos?.find((p) => p.egoID === coverEgoId)
    const fromNotes = topic.notes?.flatMap((n) => n.egoList || []).find((e) => e.egoID === coverEgoId)
    const pickedUrl =
      fromPhotos?.photoFullURL || fromPhotos?.photoAlbumURL || fromNotes?.photoAlbumURL || fromPhotos?.photoThumbURL
    if (pickedUrl) return pickedUrl
  }
  return (
    topic.latestEgo?.photoAlbumURL ||
    topic.photos?.[0]?.photoFullURL ||
    topic.photos?.[0]?.photoAlbumURL ||
    topic.firstNote?.egoList?.[0]?.photoAlbumURL ||
    topic.notes?.find((n) => n.egoList?.length)?.egoList?.[0]?.photoAlbumURL ||
    topic.photos?.[0]?.photoThumbURL ||
    undefined
  )
}

function coverFromTopic(topic: Topic): string | undefined {
  return topicCoverUrl(topic)
}

function photoCountFromTopic(topic: Topic): number {
  const fromField = Number(topic.photoCount || topic.mediaCount || 0)
  if (fromField > 0) return fromField
  const notes = topic.notes || []
  const firstId = topic.firstNote?.noteID
  return notes
    .filter((n) => !firstId || n.noteID !== firstId)
    .reduce((sum, n) => sum + (n.egoList?.length || 0), 0)
}

export function topicToAlbumSummary(topic: Topic): AlbumSummary {
  return {
    id: topic.topicID,
    title: topic.topicName || 'Untitled album',
    coverUrl: coverFromTopic(topic),
    photoCount: photoCountFromTopic(topic),
    updatedAt: topic.dateUpdated || topic.dateLastInteracted,
    privacy: mapPrivacy(topic.visibility),
  }
}

/**
 * Flatten note egos into grid photos.
 * Excludes firstNote (cover/placeholder) — matches babylon_meshdom AlbumContent.
 * Resolves full URLs from topic.photos when egoList only has album-size URLs.
 * Also merges any topic.photos egos missing from notes (API sometimes omits egoList entries).
 */
export function notePhotosToAlbumPhotos(
  notes: Note[] = [],
  firstNoteId?: string,
  topicPhotos?: Topic['photos'],
): AlbumPhoto[] {
  const byEgo = new Map((topicPhotos || []).map((p) => [p.egoID, p]))
  const photos: AlbumPhoto[] = []
  const seenEgo = new Set<string>()

  const pushEgo = (
    noteId: string,
    ego: {
      egoID?: string
      photoAlbumURL?: string
      photoFullURL?: string
    },
    i: number,
    liked: boolean,
    likeCount: number,
    commentCount: number,
  ) => {
    const topicPhoto = ego.egoID ? byEgo.get(ego.egoID) : undefined
    const albumUrl = ego.photoAlbumURL || topicPhoto?.photoAlbumURL || ''
    const full =
      ego.photoFullURL ||
      topicPhoto?.photoFullURL ||
      (albumUrl ? getFullImageURL(albumUrl) : '') ||
      topicPhoto?.photoThumbURL ||
      ''
    if (!full && !albumUrl) return
    if (ego.egoID) {
      if (seenEgo.has(ego.egoID)) return
      seenEgo.add(ego.egoID)
    }

    photos.push({
      id: `${noteId}-${ego.egoID || i}`,
      noteId,
      egoId: ego.egoID,
      url: full || albumUrl,
      thumbUrl: albumUrl || full,
      liked,
      likeCount,
      commentCount,
    })
  }

  notes.forEach((note) => {
    // Match meshdom AlbumContent: skip cover/meta firstNote (deduped via topic.photos fallback)
    if (firstNoteId && note.noteID === firstNoteId) return
    ;(note.egoList || []).forEach((ego, i) => {
      pushEgo(
        note.noteID,
        ego,
        i,
        note.isThumbsUp === 1,
        Number(note.thumbsUp || 0),
        Number(note.comments || 0),
      )
    })
  })

  // Fallback: topic.photos may list egos not present on the current notes page
  ;(topicPhotos || []).forEach((p, i) => {
    if (!p.egoID || seenEgo.has(p.egoID)) return
    pushEgo(
      `topic-photo`,
      {
        egoID: p.egoID,
        photoAlbumURL: p.photoAlbumURL,
        photoFullURL: p.photoFullURL,
      },
      i,
      false,
      0,
      0,
    )
  })

  return photos
}

/**
 * List-time gallery mapping — everything derivable from the `topicList`
 * response alone (no extra request). `topicXData` is the wizard-create
 * payload (`sourceAlbumId`, `printCount`, `wizard.sceneId`/`slots`) written
 * by `useCreateGalleryFromAlbum`; it does NOT contain the live
 * `StudioSceneState` (room mode/colors, actually-hung prints) — that lives
 * in a `studio3dSceneStateV1` note, fetched separately and merged in by
 * `useGalleryLibrary`'s progressive enrichment pass. Until that resolves,
 * a `wizard.sceneId` that matches a Classic template gives us a real,
 * already-shipped room screenshot for free; anything else reads as
 * `studio`/`corridor`, which is this app's actual default for an unopened
 * gallery (`ROOM_DEFAULTS` in gallery-studio-3d/constants.ts) — not a guess.
 */
/**
 * Real hung-print thumbnails straight from the wizard's seed data — every
 * wizard-created gallery's `topicXData.wizard.slots` already carries each
 * slot's real `url`/`thumbUrl` (see `GalleryWizardScreen`), no extra
 * request needed. Empty for freeform Studio3D-built galleries (no wizard
 * step) or slots not yet assigned a photo.
 */
function artsFromWizardSlots(slots?: unknown): GalleryArtPreview[] {
  if (!Array.isArray(slots)) return []
  return slots
    .filter((s): s is { photoId?: string; url?: string; thumbUrl?: string } => Boolean(s?.photoId))
    .map((s) => ({ img: s.url || s.thumbUrl || '' }))
    .filter((a) => a.img)
    .slice(0, 3)
}

/**
 * Real hung-print thumbnails from the room's own `studio3dSceneStateV1`
 * note — the format Studio3D itself saves/reads (see `sceneState.ts`,
 * `useGalleryLibrary`'s `parseSceneState`). `topic.latestNote` (present on
 * *every* topicList response, mini or full — no extra request) happens to
 * be this exact note whenever it's the most recently touched one, which
 * covers most freeform (non-wizard) galleries. When some other note (a
 * pin, a comment) was touched more recently, this returns nothing — the
 * caller elsewhere (`useGalleryLibrary`, `useStudio3DData`) still does the
 * authoritative full-note fetch for anything that actually needs certainty.
 */
function parseSceneStateNote(note?: Note): StudioSceneState | null {
  if (!note || note.noteName !== STUDIO3D_NOTE_NAME || !note.noteText) return null
  try {
    const parsed = JSON.parse(note.noteText)
    if (parsed?.version === 1 && Array.isArray(parsed.arts)) return parsed as StudioSceneState
  } catch {
    /* ignore malformed note text */
  }
  return null
}

/**
 * `latestNote` first (present on every mini/full response for free) — only
 * a real hit when the scene note happens to be the most recently touched
 * one. Falls back to a full `notes` array when the caller has one (e.g.
 * Discover's bounded enrichment fetch, `useGalleryLibrary`'s per-topic
 * load) — scans every note since a pin/comment touched more recently would
 * otherwise hide the scene note from `latestNote` alone.
 */
function sceneStateFromTopic(topic: Topic): StudioSceneState | null {
  return parseSceneStateNote(topic.latestNote) || (topic.notes || []).map(parseSceneStateNote).find(Boolean) || null
}

export function topicToGallerySummary(topic: Topic): GallerySummary {
  let sourceAlbumId: string | undefined
  let printCount = 0
  let wizardSceneId: number | undefined
  let arts: GalleryArtPreview[] = []
  try {
    if (topic.topicXData) {
      const parsed = JSON.parse(topic.topicXData)
      sourceAlbumId = parsed?.sourceAlbumId != null ? String(parsed.sourceAlbumId) : undefined
      printCount = Number(parsed?.printCount) || 0
      const slots = Array.isArray(parsed?.wizard?.slots) ? parsed.wizard.slots : undefined
      if (!printCount && slots) printCount = slots.filter((s: { photoId?: string }) => s?.photoId).length
      if (parsed?.wizard?.sceneId != null) wizardSceneId = Number(parsed.wizard.sceneId)
      arts = artsFromWizardSlots(slots)
    }
  } catch {
    /* ignore malformed xdata */
  }

  const classicVariant = wizardSceneId != null ? getClassicVariant(wizardSceneId) : undefined

  // Freeform Studio3D galleries have no wizard slots — try the live scene
  // note next (see sceneStateFromTopic above).
  let wallColor: string | undefined
  let floorColor: string | undefined
  if (arts.length === 0) {
    const scene = sceneStateFromTopic(topic)
    if (scene) {
      arts = scene.arts.slice(0, 3).map((a) => ({ img: a.img, ratio: a.ratio }))
      printCount = scene.arts.length || printCount
      wallColor = scene.room.wallColor
      floorColor = scene.room.floorColor
    }
  }

  return {
    id: topic.topicID,
    title: topic.topicName || 'Untitled gallery',
    sourceAlbumId,
    updatedAt: topic.dateUpdated || topic.dateLastInteracted,
    privacy: mapPrivacy(topic.visibility),
    printCount: printCount || classicVariant?.printCount || 0,
    mode: classicVariant ? 'classic' : 'studio',
    classicTemplateId: classicVariant?.id,
    wallColor,
    floorColor,
    arts,
    sceneLoaded: false,
  }
}

/**
 * Applies the owner's saved drag-to-reorder (task #10) on top of the
 * natural upload-batch order `notePhotosToAlbumPhotos` returns. Photos
 * whose `egoId` isn't in `order` (new uploads since the last reorder, or
 * the rare photo with no `egoId` at all) keep their natural relative order,
 * appended after every explicitly-ordered photo — so a fresh upload always
 * shows up rather than silently vanishing to some mid-list position.
 */
export function sortPhotosByOrder(photos: AlbumPhoto[], order?: string[]): AlbumPhoto[] {
  if (!order || order.length === 0) return photos
  const rank = new Map(order.map((egoId, i) => [egoId, i]))
  const ranked: AlbumPhoto[] = []
  const rest: AlbumPhoto[] = []
  photos.forEach((p) => {
    if (p.egoId && rank.has(p.egoId)) ranked.push(p)
    else rest.push(p)
  })
  ranked.sort((a, b) => rank.get(a.egoId as string)! - rank.get(b.egoId as string)!)
  return [...ranked, ...rest]
}

export function topicToAlbumDetail(topic: Topic, isOwner?: boolean): AlbumDetail {
  const summary = topicToAlbumSummary(topic)
  return {
    ...summary,
    description: topic.topicDescription,
    photos: sortPhotosByOrder(
      notePhotosToAlbumPhotos(topic.notes, topic.firstNote?.noteID, topic.photos),
      parsePhotoOrder(topic.topicXData),
    ),
    isOwner,
    ownerId: topic.userID != null ? String(topic.userID) : undefined,
    shareUrl:
      typeof window !== 'undefined' ? `${window.location.origin}/a/${topic.topicID}` : undefined,
    allowDownload: parseAllowDownload(topic.topicXData),
    galleryId: parseGalleryId(topic.topicXData),
    coverEgoId: parseCoverEgoId(topic.topicXData),
    fullResolutionUploads: parseFullResolutionUploads(topic.topicXData),
    topicXData: topic.topicXData || '',
    cliqueTypes: topic.cliqueTypes || [],
  }
}
